import { useCallback, useState } from "react";
import { downloadCreditDebitNotePdf } from "../../services";
import { pushGlobalError } from "../../errors/globalError";
import { saveBlobResponse } from "../../utils/downloadBlob";
import { PDF_DOWNLOAD_ERROR_MESSAGE } from "../../utils/hooks/usePdfDownload";

/** A failure that came back with an HTTP status already has the interceptor's message. */
const handledByInterceptor = (error: unknown) =>
  typeof (error as { response?: { status?: number } } | null)?.response?.status === "number";

const isPdfBlob = (data: unknown): boolean =>
  data instanceof Blob && data.size > 0 && !data.type.includes("application/json");

/**
 * Downloads a note's PDF. The endpoint is authenticated, so the file is fetched as a blob
 * through the shared axios client and saved under the server's filename (`<note_no>.pdf`).
 */
export function useNotePdf(noteUuid: string, noteNo: string) {
  const [downloading, setDownloading] = useState<boolean>(false);

  const download = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const response = await downloadCreditDebitNotePdf(noteUuid);
      if (!isPdfBlob(response.data)) throw new Error("Invalid PDF file response");
      saveBlobResponse(response, `${noteNo}.pdf`);
    } catch (error) {
      if (!handledByInterceptor(error)) pushGlobalError(PDF_DOWNLOAD_ERROR_MESSAGE);
    } finally {
      setDownloading(false);
    }
  }, [downloading, noteUuid, noteNo]);

  return { downloading, download };
}
